import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { useClients } from '../clients';
import { useTasks } from '../../hooks/useTasks';
import { useSupportTickets } from '../../hooks/useSupportTickets';
import { useInvoices, Invoice } from '../../hooks/useInvoices';

export interface DashboardStats {
  totalClients: number;
  activeTasks: number;
  openTickets: number;
  pendingInvoices: number;
  totalRevenue?: number;
  monthlyRevenue?: number;
  pendingTodos?: number;
  overdueTasks?: number;
}

export interface Activity {
  id?: number;
  message?: string;
  description?: string;
  userName?: string;
  createdAt?: string;
}

export interface DailyDashboardItem {
  id: number;
  title: string;
  subtitle?: string;
  date?: string;
  amount?: number;
  link?: string;
  status?: string;
}

export interface DailyDashboardSection<T = DailyDashboardItem> {
  count: number;
  items: T[];
}

export interface DailyDashboardResponse {
  meta: {
    scope: 'me' | 'team';
    range: 'today' | 'week';
    startDate: string;
    endDate: string;
  };
  agenda: {
    appointments: DailyDashboardSection;
    reminders: DailyDashboardSection;
    calls: DailyDashboardSection;
  };
  tasks: {
    dueToday: DailyDashboardSection;
    overdue: DailyDashboardSection;
    todayInvoicedMinutes?: number;
    completedCount?: number;
  };
  todos: DailyDashboardSection;
  leads: {
    dueToday: DailyDashboardSection;
    overdue: DailyDashboardSection;
  };
  support: {
    assigned: DailyDashboardSection;
    unassigned: DailyDashboardSection;
  };
  communications: {
    unreadEmails: number;
    recentEmails: DailyDashboardSection;
  };
  operations: {
    projectsDue: DailyDashboardSection;
    servicesExpiring: DailyDashboardSection;
    packagesExpiring: DailyDashboardSection;
  };
  finance: {
    overdueInvoices: DailyDashboardSection;
    recentPayments: DailyDashboardSection;
    recentExpenses: DailyDashboardSection;
  };
}

/** Backend returns team stats at top level and user stats in user.overview when user is logged in */
interface DashboardStatsResponse extends DashboardStats {
  total?: { overview?: { totalTodos?: number; overdueTasks?: number } };
  user?: { overview?: { openTickets?: number; openTasks?: number; totalTodos?: number; overdueTasks?: number } };
}

export const useDashboardStats = (scope?: 'myWork' | 'team') => {
  const { data, error, isLoading, mutate } = useSWR<DashboardStatsResponse>(
    '/dashboard/stats',
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    }
  );

  // Fallback to other hooks if dashboard stats endpoint doesn't have all data
  const { total: totalClients } = useClients();
  const { total: totalTasks } = useTasks();
  const { total: totalTickets } = useSupportTickets();
  const { invoices: invoicesData } = useInvoices(undefined, 'pending');

  // Ensure invoices is always an array
  const invoices = Array.isArray(invoicesData) ? invoicesData : [];

  // Calculate pending invoices total
  const pendingInvoicesTotal = invoices
    .filter((inv: Invoice) => !inv.invoiceIncome || inv.invoiceIncome === 0)
    .reduce((sum: number, inv: Invoice) => sum + (inv.invoiceAmount || 0), 0);

  const useMyWorkOpenTickets = scope === 'myWork' && data?.user?.overview;
  const openTickets = useMyWorkOpenTickets
    ? (data!.user!.overview!.openTickets ?? data?.openTickets ?? totalTickets ?? 0)
    : (data?.openTickets ?? totalTickets ?? 0);

  const useMyWorkActiveTasks = scope === 'myWork' && data?.user?.overview;
  const activeTasks = useMyWorkActiveTasks
    ? (data!.user!.overview!.openTasks ?? data?.activeTasks ?? totalTasks ?? 0)
    : (data?.activeTasks ?? totalTasks ?? 0);

  const useMyWorkPendingTodos = scope === 'myWork' && data?.user?.overview;
  const pendingTodos = useMyWorkPendingTodos
    ? (data!.user!.overview!.totalTodos ?? data?.total?.overview?.totalTodos ?? 0)
    : (data?.total?.overview?.totalTodos ?? 0);

  const useMyWorkOverdueTasks = scope === 'myWork' && data?.user?.overview;
  const overdueTasks = useMyWorkOverdueTasks
    ? (data!.user!.overview!.overdueTasks ?? data?.total?.overview?.overdueTasks ?? 0)
    : (data?.total?.overview?.overdueTasks ?? 0);

  return {
    stats: {
      totalClients: data?.totalClients || totalClients || 0,
      activeTasks,
      openTickets,
      pendingInvoices: data?.pendingInvoices || pendingInvoicesTotal || 0,
      totalRevenue: data?.totalRevenue,
      monthlyRevenue: data?.monthlyRevenue,
      pendingTodos,
      overdueTasks,
    },
    isLoading,
    isError: error,
    mutate,
  };
};

export const useRecentActivity = (limit: number = 10) => {
  const { data, error, isLoading, mutate } = useSWR<Activity[]>(
    `/dashboard/recent-activity?limit=${limit}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 60000, // Refresh every minute
    }
  );

  return {
    activities: data || [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useDailyDashboard = (scope: 'me' | 'team' = 'me', range: 'today' | 'week' = 'today') => {
  const params = new URLSearchParams({ scope, range });
  const { data, error, isLoading, mutate } = useSWR<DailyDashboardResponse>(
    `/dashboard/daily?${params.toString()}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 60000,
    }
  );  return {
    data,
    isLoading,
    isError: error,
    mutate,
  };
};